import { useMemo, useState } from 'react';
import { makeStyles, createStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import { Formik, Form } from 'formik';
import { urls, route } from '../route';
import { useNavigate } from 'react-router-dom';
import { UserProfilePayload } from '../models/UserProfilePayload';
import { UserScheduledWorkout } from '../models/UserScheduledWorkout';
import { useLoadData } from '../utils';
import {
  postApiUserUpdate,
  getApiUserUserid,
  putApiUsersLicenceExpiration,
  getUserScheduledWorkouts,
  postUserRescheduleMissedWorkouts,
} from '../services/user';
import { UpdateUserPayload } from '../models/UpdateUserPayload';
import { useParams } from 'react-router-dom';
import { Chip, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { PaymentPlans, Sex, formatRequestErrorMessage, isFetchError, showRequestToast } from '../services/utils';
import { ScheduleEditDialog } from '../components/ScheduleEditDialog';
import { ConfirmDialog } from '../components/ConfirmDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3, 2),
    },
    tableContainer: {
      marginTop: theme.spacing(2),
      maxHeight: 480,
    },
  })
);

function formatScheduledDate(date: string) {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getScheduleStatusLabel(workout: UserScheduledWorkout) {
  if (workout.status === 1) {
    return { label: 'Completed', color: 'success' as const };
  }

  const workoutDate = new Date(workout.date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  workoutDate.setHours(0, 0, 0, 0);

  if (workoutDate < today) {
    return { label: 'Missed', color: 'error' as const };
  }

  return { label: 'Pending', color: 'warning' as const };
}

function getParentWorkoutLabel(workout: UserScheduledWorkout) {
  if (!workout.parentWorkoutName) {
    return '-';
  }

  if (workout.parentPhaseName) {
    return `${workout.parentWorkoutName} (${workout.parentPhaseName})`;
  }

  return workout.parentWorkoutName;
}

const SUBSCRIPTION_TYPES = new Map<string, string>([
  ['STRIPE', 'Stripe'],
  ['PAYPAL', 'Paypal'],
  ['com.romcoach.monthly.subs', 'Mobile'],
  ['com.romcoach.yearly.subs', 'Mobile'],
  ['com.romcoach.monthly', 'Mobile'],
]);

export const UserEdit = () => {
  const classes = useStyles();
  const history = useNavigate();
  const p = useParams();
  const [noDate, setNoDate] = useState(false);
  const [emailNotification, setEmailNotifications] = useState(false);
  const [pushNotification, setpushNotification] = useState(false);
  const [user, setUser] = useState<UserProfilePayload>();
  const [scheduledWorkouts, setScheduledWorkouts] = useState<
    UserScheduledWorkout[]
  >([]);
  const [scheduledWorkoutsLoading, setScheduledWorkoutsLoading] = useState(true);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleConfirmOpen, setRescheduleConfirmOpen] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState<UserScheduledWorkout | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  const loadScheduledWorkouts = async (userId: number) => {
    setScheduledWorkoutsLoading(true);
    try {
      const response = await getUserScheduledWorkouts(userId);
      setScheduledWorkouts(response.items ?? []);
    } finally {
      setScheduledWorkoutsLoading(false);
    }
  };

  useLoadData(async () => {
    const result = await getApiUserUserid(+p.id!);
    setNoDate(result.licenceExpiration ? false : true);
    setEmailNotifications(result.profile?.emailNotification!);
    setpushNotification(result.profile?.pushNotification!);
    setUser({
      ...result,
      licenceExpiration: (
        result.licenceExpiration ?? new Date().toISOString()
      ).split('T')[0],
    });
    await loadScheduledWorkouts(+p.id!);
  });

  const missedWorkoutsCount = useMemo(
    () =>
      scheduledWorkouts.filter((workout) => {
        if (workout.status === 1) return false;
        const workoutDate = new Date(workout.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        workoutDate.setHours(0, 0, 0, 0);
        return workoutDate < today;
      }).length,
    [scheduledWorkouts]
  );

  async function handleScheduleSaved() {
    if (p.id) {
      await loadScheduledWorkouts(+p.id);
    }
  }

  function openScheduleEditor(workout: UserScheduledWorkout) {
    setSelectedWorkout(workout);
    setScheduleDialogOpen(true);
  }

  function closeScheduleEditor() {
    setScheduleDialogOpen(false);
    setSelectedWorkout(null);
  }

  async function handleRescheduleMissedWorkouts() {
    if (!p.id || rescheduling) return;

    setRescheduling(true);
    try {
      const result = await postUserRescheduleMissedWorkouts(+p.id);
      showRequestToast(
        `Rescheduled ${result.processedSchedules} schedule(s). Created ${result.notifiedUsers} notification(s).`,
        'success'
      );
      setRescheduleConfirmOpen(false);
      await loadScheduledWorkouts(+p.id);
    } catch (error) {
      if (!isFetchError(error)) {
        showRequestToast(formatRequestErrorMessage(error), 'error');
      }
    } finally {
      setRescheduling(false);
    }
  }

  async function onSubmit(e: UpdateUserPayload) {
    if (p.id) {
      const licenceExpiration = noDate
      ? undefined
      : new Date(e?.licenceExpiration ?? '').toISOString();
      try {
        if (licenceExpiration) {
          await putApiUsersLicenceExpiration(+p.id, licenceExpiration);
        }
      } catch (e) {
        console.log(e);
      }

      try {
        if (p.id) {
          await postApiUserUpdate({
            id: +p.id,
            licenceExpiration,
            emailNotification: emailNotification,
            pushNotification: pushNotification,
          });
        }
        history(route(urls.userList));
      } catch (e) {
        console.log(e);
      }
    }
  }

  if (!user) return <div></div>;
  return (
    <Container>
      <Paper className={classes.root}>
        <Formik<UserProfilePayload> initialValues={user} onSubmit={onSubmit}>
          {({ values, handleChange, setFieldValue }) => {
            return (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h4" gutterBottom>
                      {p.id ? `Edit ${user.email}` : ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={6}>
                      <Grid item md={4} sm={6}>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="primary"
                        >
                          Submit
                        </Button>
                      </Grid>
                      <Grid item md={4} sm={6}>
                        <Button
                          onClick={(e) => {
                            history(route(urls.userList));
                          }}
                          fullWidth
                          variant="outlined"
                          color="primary"
                        >
                          Back
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    style={{ margin: 0 }}
                    container
                    spacing={3}
                    md={8}
                    sm={12}
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6">General</Typography>
                    </Grid>
                    <Grid item md={6} sm={12}>
                      <TextField
                        fullWidth
                        label="First Name"
                        variant="outlined"
                        value={values?.profile?.firstName ?? '-'}
                        id={'firstName'}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item md={6} sm={12}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        variant="outlined"
                        value={values?.profile?.lastName ?? '-'}
                        id={'lastName'}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item md={6} sm={12}>
                      <TextField
                        fullWidth
                        label="Sex"
                        variant="outlined"
                        value={Sex[values?.profile?.gender ?? 0]}
                        id={'gender'}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item md={6} sm={12}>
                      <TextField
                        fullWidth
                        label="Date of birth"
                        variant="outlined"
                        value={values?.profile?.birthDate?.split('T')[0] ?? '-'}
                        id={'dob'}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    style={{ margin: 0 }}
                    container
                    spacing={3}
                    md={8}
                    sm={12}
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6">Subscription</Typography>
                    </Grid>
                    <Grid item sm={6} style={{ paddingTop: 30 }}>
                      <span style={{ paddingTop: 15 }}>Status: </span>
                      {!values.licenceExpiration || noDate ? (
                        <Chip label="Free" color="warning" />
                      ) : values.licenceExpiration &&
                        new Date(values.licenceExpiration) >=
                          new Date(Date.now()) ? (
                        <Chip label="Active" color="success" />
                      ) : (
                        <Chip label="Expired" color="error" />
                      )}
                    </Grid>
                    <Grid item sm={6}>
                      <div style={{ float: 'right' }}>
                        <span style={{ paddingTop: 15 }}>No License: </span>
                        <Checkbox
                          inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                          value={noDate}
                          checked={noDate}
                          onChange={() => {
                            setNoDate(!noDate);
                          }}
                        />
                      </div>
                    </Grid>

                    <Grid item md={6} sm={12}>
                      <TextField
                        fullWidth
                        label="Subscription Type"
                        variant="outlined"
                        value={
                          !noDate
                            ? user?.subscription?.product_id ?? 'Custom'
                            : 'Free'
                        }
                        id={'subscriptionType'}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid item md={6} sm={12}>
                      <TextField
                        disabled={noDate}
                        fullWidth
                        type="date"
                        label="Expiration Date"
                        variant="outlined"
                        inputProps={{
                          autoComplete: 'off',
                          'data-1p-ignore': true,
                          'data-lpignore': 'true',
                        }}
                        value={
                          values.licenceExpiration ??
                          new Date().toISOString().split('T')[0]
                        }
                        id={'licenceExpiration'}
                        InputLabelProps={{ shrink: true }}
                        onChange={(event) => {
                          setFieldValue(
                            'licenceExpiration',
                            event.target.value.toString()
                          );
                        }}
                      />
                    </Grid>

                    <Grid item sm={12}>
                      <TextField
                        fullWidth
                        label="Subscription ID"
                        variant="outlined"
                        value={!noDate ? user?.subscription?.token ?? '-' : '-'}
                        id={'subscriptionId'}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid item md={6} sm={12}>
                      <TextField
                        fullWidth
                        label="Plan"
                        variant="outlined"
                        value={
                          !noDate
                            ? PaymentPlans[
                                user?.subscription
                                  ?.product_id as keyof typeof PaymentPlans
                              ] || '-'
                            : 'Free'
                        }
                        id={'subscriptionPlan'}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>

                    <Grid
                      container
                      spacing={3}
                      style={{ margin: 0, marginBottom: '10px' }}
                    ></Grid>
                    <Grid item sm={12} style={{ marginTop: '30px' }}>
                      <Typography variant="h6">Notifications</Typography>
                    </Grid>
                    <Grid item sm={12} md={4}>
                      Email notifications:
                      <Checkbox
                        inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                        value={user!.profile!.emailNotification}
                        checked={emailNotification}
                        onChange={() => {
                          setEmailNotifications(!emailNotification);
                        }}
                      />
                    </Grid>
                    <Grid item sm={12} md={4}>
                      Push notifications:
                      <Checkbox
                        inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                        value={user!.profile!.pushNotification}
                        checked={pushNotification}
                        onChange={() => {
                          setpushNotification(!pushNotification);
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6">Scheduled Workouts</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {missedWorkoutsCount} missed workout(s) found.
                    </Typography>
                    <Grid container spacing={2} style={{ marginTop: 8 }}>
                      <Grid item md={4} sm={6}>
                        <Button
                          type="button"
                          fullWidth
                          variant="outlined"
                          color="primary"
                          disabled={
                            rescheduling ||
                            scheduledWorkoutsLoading ||
                            missedWorkoutsCount === 0
                          }
                          onClick={() => setRescheduleConfirmOpen(true)}
                          startIcon={
                            rescheduling ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : undefined
                          }
                        >
                          {rescheduling
                            ? 'Rescheduling...'
                            : 'Reschedule All Missed Workouts'}
                        </Button>
                      </Grid>
                    </Grid>
                    <TableContainer className={classes.tableContainer}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Workout</TableCell>
                            <TableCell>Parent Program</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {scheduledWorkoutsLoading ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                <CircularProgress size={24} />
                              </TableCell>
                            </TableRow>
                          ) : scheduledWorkouts.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                No scheduled workouts found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            scheduledWorkouts.map((workout) => {
                              const status = getScheduleStatusLabel(workout);
                              return (
                                <TableRow key={workout.id}>
                                  <TableCell>
                                    {formatScheduledDate(workout.date)}
                                  </TableCell>
                                  <TableCell>{workout.workoutName}</TableCell>
                                  <TableCell>
                                    {getParentWorkoutLabel(workout)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={status.label}
                                      color={status.color}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      aria-label={`Edit ${workout.editLabel}`}
                                      size="small"
                                      onClick={() => openScheduleEditor(workout)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Paper>
      {p.id ? (
        <>
          <ScheduleEditDialog
            open={scheduleDialogOpen}
            userId={+p.id}
            workout={selectedWorkout}
            onClose={closeScheduleEditor}
            onSaved={handleScheduleSaved}
          />
          <ConfirmDialog
            open={rescheduleConfirmOpen}
            title="Reschedule all missed workouts?"
            message={`This will reschedule ${missedWorkoutsCount} missed workout(s) for this user and send notifications where applicable. Continue?`}
            confirmLabel="Reschedule"
            loading={rescheduling}
            onConfirm={handleRescheduleMissedWorkouts}
            onCancel={() => {
              if (!rescheduling) {
                setRescheduleConfirmOpen(false);
              }
            }}
          />
        </>
      ) : null}
    </Container>
  );
};
